import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import Sidebar from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { motion } from 'framer-motion';
import {
  Loader2, Plus, Calendar, Dog, Users, Camera, Trophy,
  Upload, Shield, Edit2, Trash2, Copy, ExternalLink, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportAdminPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [tab, setTab] = useState('seasons');
  const [seasons, setSeasons] = useState([]);
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [captainTokens, setCaptainTokens] = useState([]);
  const [importJobs, setImportJobs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create forms
  const [showCreateSeason, setShowCreateSeason] = useState(false);
  const [newSeason, setNewSeason] = useState({ name: '', year: new Date().getFullYear(), sport_type: 'flyball', description: '' });
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: '', season_id: '', date_start: '', location: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const [seasonsRes, eventsRes, teamsRes, tokensRes, jobsRes, ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/sport/seasons`),
        axios.get(`${API}/sport/events`),
        axios.get(`${API}/sport/teams`),
        axios.get(`${API}/sport/captain/tokens`),
        axios.get(`${API}/sport/import/jobs`),
        axios.get(`${API}/sport/shop/orders`).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API}/sport/shop/orders/stats/summary`).catch(() => ({ data: null })),
      ]);
      setSeasons(seasonsRes.data.seasons);
      setEvents(eventsRes.data.events);
      setTeams(teamsRes.data.teams);
      setCaptainTokens(tokensRes.data.tokens);
      setImportJobs(jobsRes.data.jobs);
      setOrders(ordersRes.data.orders);
      setOrderStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load sport data:', err);
    } finally {
      setLoading(false);
    }
  };

  const createSeason = async () => {
    try {
      await axios.post(`${API}/sport/seasons`, newSeason);
      toast.success(language === 'pl' ? 'Sezon utworzony' : 'Season created');
      setShowCreateSeason(false);
      setNewSeason({ name: '', year: new Date().getFullYear(), sport_type: 'flyball', description: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const createEvent = async () => {
    try {
      await axios.post(`${API}/sport/events`, newEvent);
      toast.success(language === 'pl' ? 'Zawody utworzone' : 'Event created');
      setShowCreateEvent(false);
      setNewEvent({ name: '', season_id: '', date_start: '', location: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const createCaptainInvite = async (teamId) => {
    try {
      const res = await axios.post(`${API}/sport/captain/invite`, { team_id: teamId });
      const url = `${window.location.origin}${res.data.url}`;
      navigator.clipboard.writeText(url);
      toast.success(language === 'pl' ? 'Link skopiowany do schowka' : 'Link copied to clipboard');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar activePath="/sport" />
        <main className="lg:ml-64 p-6 lg:p-10 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin" />
        </main>
      </div>
    );
  }

  const tabs = [
    { key: 'seasons', icon: Trophy, pl: 'Sezony', en: 'Seasons', count: seasons.length },
    { key: 'events', icon: Calendar, pl: 'Zawody', en: 'Events', count: events.length },
    { key: 'teams', icon: Users, pl: 'Druzyny', en: 'Teams', count: teams.length },
    { key: 'captains', icon: Shield, pl: 'Kapitanowie', en: 'Captains', count: captainTokens.length },
    { key: 'orders', icon: CreditCard, pl: 'Zamowienia', en: 'Orders', count: orders.length },
    { key: 'imports', icon: Upload, pl: 'Importy', en: 'Imports', count: importJobs.length },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/sport" />
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">{language === 'pl' ? 'Sport' : 'Sport'}</h1>
          <a href="/sport" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-1" />
              {language === 'pl' ? 'Podglad publiczny' : 'Public view'}
            </Button>
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { icon: Trophy, value: seasons.length, label: language === 'pl' ? 'Sezony' : 'Seasons' },
            { icon: Calendar, value: events.length, label: language === 'pl' ? 'Zawody' : 'Events' },
            { icon: Users, value: teams.length, label: language === 'pl' ? 'Druzyny' : 'Teams' },
            { icon: Dog, value: teams.reduce((sum, t) => sum + (t.dog_count || 0), 0), label: language === 'pl' ? 'Psy' : 'Dogs' },
            { icon: Camera, value: events.reduce((sum, e) => sum + (e.photo_count || 0), 0), label: language === 'pl' ? 'Zdjecia' : 'Photos' },
          ].map((stat, idx) => (
            <Card key={idx}>
              <CardContent className="p-4 text-center">
                <stat.icon className="w-6 h-6 mx-auto mb-1 text-muted-foreground" />
                <p className="text-2xl font-bold">{stat.value}</p>
                <p className="text-xs text-muted-foreground">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {language === 'pl' ? t.pl : t.en}
              <span className="text-xs opacity-60">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Seasons Tab */}
        {tab === 'seasons' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowCreateSeason(!showCreateSeason)}>
                <Plus className="w-4 h-4 mr-1" /> {language === 'pl' ? 'Nowy sezon' : 'New season'}
              </Button>
            </div>
            {showCreateSeason && (
              <Card className="mb-6">
                <CardContent className="p-4 space-y-3">
                  <Input placeholder={language === 'pl' ? 'Nazwa sezonu' : 'Season name'} value={newSeason.name} onChange={e => setNewSeason(p => ({ ...p, name: e.target.value }))} />
                  <div className="flex gap-3">
                    <Input type="number" placeholder="Rok" value={newSeason.year} onChange={e => setNewSeason(p => ({ ...p, year: parseInt(e.target.value) }))} className="w-32" />
                    <Input placeholder="Sport type" value={newSeason.sport_type} onChange={e => setNewSeason(p => ({ ...p, sport_type: e.target.value }))} className="w-40" />
                  </div>
                  <Input placeholder={language === 'pl' ? 'Opis' : 'Description'} value={newSeason.description} onChange={e => setNewSeason(p => ({ ...p, description: e.target.value }))} />
                  <Button onClick={createSeason}>{language === 'pl' ? 'Utworz' : 'Create'}</Button>
                </CardContent>
              </Card>
            )}
            <div className="space-y-3">
              {seasons.map(s => (
                <Card key={s.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{s.name}</h4>
                      <p className="text-sm text-muted-foreground">{s.sport_type} {s.year} — {s.status}</p>
                    </div>
                    <span className="text-sm text-muted-foreground">{s.event_count || 0} events</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Events Tab */}
        {tab === 'events' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowCreateEvent(!showCreateEvent)}>
                <Plus className="w-4 h-4 mr-1" /> {language === 'pl' ? 'Nowe zawody' : 'New event'}
              </Button>
            </div>
            {showCreateEvent && (
              <Card className="mb-6">
                <CardContent className="p-4 space-y-3">
                  <Input placeholder={language === 'pl' ? 'Nazwa zawodow' : 'Event name'} value={newEvent.name} onChange={e => setNewEvent(p => ({ ...p, name: e.target.value }))} />
                  <div className="flex gap-3">
                    <select className="border rounded px-3 py-2 text-sm bg-background" value={newEvent.season_id} onChange={e => setNewEvent(p => ({ ...p, season_id: e.target.value }))}>
                      <option value="">{language === 'pl' ? 'Wybierz sezon' : 'Select season'}</option>
                      {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <Input type="date" value={newEvent.date_start} onChange={e => setNewEvent(p => ({ ...p, date_start: e.target.value }))} />
                  </div>
                  <Input placeholder={language === 'pl' ? 'Lokalizacja' : 'Location'} value={newEvent.location} onChange={e => setNewEvent(p => ({ ...p, location: e.target.value }))} />
                  <Button onClick={createEvent}>{language === 'pl' ? 'Utworz' : 'Create'}</Button>
                </CardContent>
              </Card>
            )}
            <div className="space-y-3">
              {events.map(e => (
                <Card key={e.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{e.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {e.location} — {e.date_start} — {e.status}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Camera className="w-3 h-3" /> {e.photo_count}</span>
                      <span className="flex items-center gap-1"><Dog className="w-3 h-3" /> {e.dog_count}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Teams Tab */}
        {tab === 'teams' && (
          <div className="space-y-3">
            {teams.map(t => (
              <Card key={t.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{t.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {t.club || ''} — {t.dog_count || 0} {language === 'pl' ? 'psow' : 'dogs'}, {t.competitor_count || 0} {language === 'pl' ? 'zawodnikow' : 'competitors'}
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => createCaptainInvite(t.id)}>
                    <Shield className="w-3 h-3 mr-1" />
                    {language === 'pl' ? 'Zapros kapitana' : 'Invite captain'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Captains Tab */}
        {tab === 'captains' && (
          <div className="space-y-3">
            {captainTokens.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {language === 'pl' ? 'Brak aktywnych zaproszen' : 'No active invitations'}
              </p>
            ) : captainTokens.map(t => (
              <Card key={t.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{t.team_name}</h4>
                    <p className="text-xs text-muted-foreground">
                      {language === 'pl' ? 'Uzycia:' : 'Uses:'} {t.used_count} | {language === 'pl' ? 'Wygasa:' : 'Expires:'} {new Date(t.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/sport/captain/${t.token}`);
                      toast.success(language === 'pl' ? 'Skopiowano' : 'Copied');
                    }}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div>
            {/* Revenue stats */}
            {orderStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { value: orderStats.total_orders, label: language === 'pl' ? 'Zamowienia' : 'Orders' },
                  { value: orderStats.paid_orders, label: language === 'pl' ? 'Oplacone' : 'Paid' },
                  { value: `${orderStats.revenue} PLN`, label: language === 'pl' ? 'Przychod' : 'Revenue' },
                  { value: orderStats.photos_sold, label: language === 'pl' ? 'Sprzedane zdjecia' : 'Photos sold' },
                ].map((stat, idx) => (
                  <Card key={idx}>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            <div className="space-y-3">
              {orders.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  {language === 'pl' ? 'Brak zamowien' : 'No orders yet'}
                </p>
              ) : orders.map(o => (
                <Card key={o.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">{o.buyer_name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {o.buyer_email} — {o.items?.length || 0} {language === 'pl' ? 'zdjec' : 'photos'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(o.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold">{o.total_amount} {o.currency}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        o.status === 'paid' ? 'bg-green-500/10 text-green-500' :
                        o.status === 'delivered' ? 'bg-blue-500/10 text-blue-500' :
                        o.status === 'cancelled' ? 'bg-red-500/10 text-red-500' :
                        'bg-yellow-500/10 text-yellow-500'
                      }`}>
                        {o.status}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Imports Tab */}
        {tab === 'imports' && (
          <div className="space-y-3">
            {importJobs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                {language === 'pl' ? 'Brak importow' : 'No imports yet'}
              </p>
            ) : importJobs.map(j => (
              <Card key={j.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{j.photographer_name || 'Import'}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      j.status === 'completed' ? 'bg-green-500/10 text-green-500' :
                      j.status === 'failed' ? 'bg-red-500/10 text-red-500' :
                      'bg-blue-500/10 text-blue-500'
                    }`}>
                      {j.status}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {j.stats?.photos || 0} photos, {j.stats?.dogs || 0} dogs, {j.stats?.teams || 0} teams
                    {j.stats?.errors > 0 && ` — ${j.stats.errors} errors`}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(j.started_at).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
